import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api/useApi';
import type {
  PluginCatalogueItem,
  PluginMember,
  PluginMemberRequest,
} from '@/shared/api/types/plugins';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/shared/components';
import { PluginProblem } from './PluginProblem';
import { PluginMemberFields } from './PluginMemberFields';
import { usePluginDialogFocus } from './usePluginDialogFocus';

export function PluginMemberEditor({
  plugin,
  member,
  onClose,
}: {
  plugin: PluginCatalogueItem;
  member: PluginMember;
  onClose: () => void;
}) {
  const api = useApi();
  const returnFocus = usePluginDialogFocus();
  const cache = useQueryClient();
  const initial: PluginMemberRequest = {
    scope: member.scope,
    propertyIds: member.propertyIds,
    actions: member.actions,
    guestCommunicationPii: member.guestCommunicationPii,
    expectedRevision: member.revision,
  };
  const [draft, setDraft] = useState(initial);
  const [reviewed, setReviewed] = useState<PluginMemberRequest | null>(null);
  const [discard, setDiscard] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const change = (next: Partial<PluginMemberRequest>) => {
    setDraft({ ...draft, ...next });
    setReviewed(null);
  };
  const preview = useMutation({
    networkMode: 'always',
    mutationFn: (body: PluginMemberRequest) =>
      api.plugins.previewMember(plugin.installation!.id, member.userId, body),
    onSuccess: (_, body) => setReviewed(body),
  });
  const save = useMutation({
    networkMode: 'always',
    mutationFn: (body: PluginMemberRequest) =>
      api.plugins.setMember(plugin.installation!.id, member.userId, body),
    onSuccess: onClose,
    onSettled: () =>
      cache.invalidateQueries({ queryKey: ['plugin-members', plugin.installation?.id] }),
  });
  const busy = preview.isPending || save.isPending;
  const close = () => {
    if (busy) return;
    if (dirty) setDiscard(true);
    else onClose();
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent onCloseAutoFocus={returnFocus}>
        <DialogHeader>
          <DialogTitle>Rechte für {member.name}</DialogTitle>
          <DialogDescription>
            Diese Freigabe gilt ausschließlich für {plugin.name}. Nach einer Änderung muss das
            Mitglied seine Identität erneut verbinden.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <PluginMemberFields plugin={plugin} draft={draft} busy={busy} change={change} />
          <p className="text-ink-soft text-sm">
            Gästekommunikation benötigt zusätzlich die wirksamen persönlichen Gästedatenrechte in
            StayOn.
          </p>
          <PluginProblem error={preview.error || save.error} />
          {reviewed === draft && (
            <p role="status" className="bg-sage/10 rounded-md p-4 text-sm">
              Geprüft:{' '}
              {draft.scope === 'None'
                ? 'Der Pluginzugriff wird entzogen.'
                : `${draft.actions.length} Aktionen für ${draft.scope === 'TenantAll' ? 'alle Apartments' : `${draft.propertyIds.length} ausgewählte Apartments`}.`}{' '}
              Globale StayOn-Rollen bleiben unverändert.
            </p>
          )}
          {discard && (
            <div role="alert" className="border-amber rounded-md border p-4">
              <p>Ungespeicherte Rechte verwerfen?</p>
              <Button type="button" size="lg" onClick={() => setDiscard(false)}>
                Weiter bearbeiten
              </Button>
              <Button type="button" size="lg" onClick={onClose}>
                Änderungen verwerfen
              </Button>
            </div>
          )}
        </DialogBody>
        <DialogFooter className="flex-col items-stretch sm:flex-row sm:items-center">
          <Button type="button" size="lg" onClick={close} disabled={busy}>
            Abbrechen
          </Button>
          {reviewed === draft ? (
            <Button
              type="button"
              variant="brand"
              size="lg"
              loading={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              Rechte speichern
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="lg"
              loading={preview.isPending}
              disabled={!dirty}
              onClick={() => preview.mutate(draft)}
            >
              Änderungen prüfen
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
